import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|Item Scoring|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Exam,
	portal: 'SP',
};

type LocNameStart = 'SPCatalogsExamsItemScoring';
export type SPCatalogsExamsItemScoringLocNames =
	| `${LocNameStart}TitleSection`
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}LabelItemName`
	| `${LocNameStart}LabelLanguage`
	| `${LocNameStart}LabelItemScoreWeight`
	| `${LocNameStart}LabelItemScoreMax`
	| `${LocNameStart}LabelComment`
	| `${LocNameStart}LabelUnscoredForms`
	| `${LocNameStart}ButtonEdit`
	| `${LocNameStart}ButtonAddItem`
	| `${LocNameStart}NotificationImportSuccess`
	| `${LocNameStart}NotificationEditSuccess`
	| `${LocNameStart}NotificationCreateSuccess`
	| `${LocNameStart}NotificationUpsertSuccess`;

export const spCatalogsExamsItemScoringLocs: LocDefs<SPCatalogsExamsItemScoringLocNames> = {
	SPCatalogsExamsItemScoringTitleSection: {
		defaultValue: 'Exam Item Scoring',
		description: 'Section Title',
		displayPath: `${pathStart}Section Title`,
		displayType: 'Title',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Title.SectionTitle',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringTitlePage: {
		defaultValue: 'Exam Item Scoring',
		description: 'Page Title',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Title.PageTitle',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringLabelItemName: {
		defaultValue: 'Name',
		description: 'Field label for: Name.',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.ColumnHeading.Name',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringLabelLanguage: {
		defaultValue: 'Language',
		description: 'Field label for: Language.',
		displayPath: `${pathStart}Language`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.ColumnHeading.Language',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringLabelItemScoreWeight: {
		defaultValue: 'Weight',
		description: 'Field label for: Weight.',
		displayPath: `${pathStart}Weight`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.ColumnHeading.Weight',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringLabelItemScoreMax: {
		defaultValue: 'Maximum',
		description: 'Field label for: Maximum.',
		displayPath: `${pathStart}Maximum`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.ColumnHeading.Maximum',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringLabelComment: {
		defaultValue: 'Comment',
		description: 'Field label for: Comment.',
		displayPath: `${pathStart}Comment`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.ColumnHeading.Comment',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringLabelUnscoredForms: {
		defaultValue: 'Unscored Forms',
		description: 'Field label for: Unscored Forms.',
		displayPath: `${pathStart}Unscored Forms`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.ColumnHeading.UnscoredForms',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringButtonEdit: {
		defaultValue: 'Edit',
		description: 'Edit button',
		displayPath: `${pathStart}Edit`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.EditItem',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringButtonAddItem: {
		defaultValue: 'Add Exam Item',
		description: 'Add Exam Item button',
		displayPath: `${pathStart}Add Exam Item`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.AddItem',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringNotificationImportSuccess: {
		defaultValue: 'Successfully imported exam items',
		description: 'Import success notification message',
		displayPath: `${pathStart}Import success`,
		displayType: 'Notification',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.NotificationMessage.ImportSuccess',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringNotificationEditSuccess: {
		defaultValue: 'Successfully updated exam item',
		description: 'Edit success notification message',
		displayPath: `${pathStart}Edit success`,
		displayType: 'Notification',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.NotificationMessage.EditSuccess',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringNotificationCreateSuccess: {
		defaultValue: 'Successfully added exam item',
		description: 'Create success notification message',
		displayPath: `${pathStart}Create success`,
		displayType: 'Notification',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.NotificationMessage.CreateSuccess',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringNotificationUpsertSuccess: {
		defaultValue: 'Successfully updated exam items',
		description: 'Upsert success notification message',
		displayPath: `${pathStart}Upsert success`,
		displayType: 'Notification',
		...sharedProperties,
	},
};

export type SPCatalogsExamsItemScoringLocType = LocMap<SPCatalogsExamsItemScoringLocNames>;
